import type { Texture } from 'pixi.js';
import type { Actor, BattleEvent, BattleSequence, DefaultAttack } from '../battle/types';
import { BATTLE_PACE_SCALE } from '../data/constants';
import type { LoadedSkillAssets, SkillBundle } from '../skills/types';
import type { AnimationAdapter } from './adapters/AnimationAdapter';
import type { BattleScene } from './BattleScene';

// POST_DAMAGE_PAUSE is the single source of "settle time" between exchanges.
// Round boundaries add zero extra delay so within-round and between-round
// gaps are equal (1s after every hit, regardless of round).
const ROUND_START_PAUSE_MS = 0;
const ROUND_END_PAUSE_MS = 0;
const BATTLE_END_PAUSE_MS = 500;
// Wall-time pause after damage lands. Intentionally NOT scaled by BATTLE_PACE_SCALE -
// this is "soak in the hit" time, the same regardless of overall pace.
const POST_DAMAGE_PAUSE_MS = 1000;

const TEXT_COLOR_ATTACK = 0xff5555;
const TEXT_COLOR_DODGE_OK = 0xfbbf24;
const TEXT_COLOR_BLOCKED = 0xfbbf24;
const TEXT_COLOR_COUNTER = 0xff8800;
const TEXT_COLOR_DAMAGE = 0xff5555;
const TEXT_COLOR_HEAL = 0x4ade80;
const TEXT_COLOR_BUFF = 0xffd000;

export type BattleRendererSkills = {
  bundles: ReadonlyMap<string, SkillBundle>;
  assets: ReadonlyMap<string, LoadedSkillAssets>;
};

export class BattleRenderer {
  // Tracks which defenders have an active block animation that needs to be
  // reverse-played in attack_end. Populated when attack_start.defenderReaction
  // is 'block', cleared after the matching attack_end.
  private pendingBlockExit = new Set<Actor>();
  // Tracks per-attacker pending attack-skill triggered on this turn. The
  // skill_trigger event fires BEFORE attack_start, so we stash the bundle here
  // and consume it in the attack_start handler to route to a custom render
  // (e.g. cannon shot) instead of the default punch flow.
  private pendingAttackSkill = new Map<Actor, SkillBundle>();
  // Block-skill stashed on its skill_trigger, consumed at the `block` event to
  // show that skill's ui-log icon (the block event carries no skillId).
  private pendingBlockSkill = new Map<Actor, SkillBundle>();
  // Attackers whose current swing was skill-driven and stationary (no lunge).
  // attack_end skips the retreat anim for these so they don't run-in-place
  // back to a position they never left.
  private inPlaceAttackers = new Set<Actor>();
  // Actors whose HP hit 0 - no more idle resets / retreats on a corpse, they
  // stay down on the last pain frame until battle_end.
  private deadActors = new Set<Actor>();
  // Set by cancel() to abort an in-progress play() (debug-mode ESC-to-quit).
  private aborted = false;
  // Pending wait() resolvers, fired immediately on cancel() so paused playback
  // bails fast instead of sitting out the post-damage pause.
  private readonly abortResolvers = new Set<() => void>();

  constructor(
    private readonly scene: BattleScene,
    private readonly adapter: AnimationAdapter,
    private readonly skills?: BattleRendererSkills,
  ) {}

  async play(seq: BattleSequence): Promise<void> {
    this.pendingBlockExit.clear();
    this.pendingAttackSkill.clear();
    this.pendingBlockSkill.clear();
    this.inPlaceAttackers.clear();
    this.deadActors.clear();
    this.aborted = false;
    for (const event of seq.events) {
      if (this.aborted) break;
      await this.handleEvent(event);
    }
  }

  get cancelled(): boolean {
    return this.aborted;
  }

  // Aborts an in-progress play(): pending waits resolve immediately and the
  // event loop bails at the next event boundary. The current awaited animation
  // still finishes (a fraction of a second); the caller tears the scene down
  // after play() resolves. Used by debug-mode ESC-to-quit.
  cancel(): void {
    this.aborted = true;
    for (const resolve of this.abortResolvers) resolve();
    this.abortResolvers.clear();
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      if (this.aborted) {
        resolve();
        return;
      }
      const done = (): void => {
        clearTimeout(timer);
        this.abortResolvers.delete(done);
        resolve();
      };
      const timer = setTimeout(done, ms);
      this.abortResolvers.add(done);
    });
  }

  // Pace-scaled pause (round boundaries) - abortable.
  private waitMs(ms: number): Promise<void> {
    return this.sleep(ms * BATTLE_PACE_SCALE);
  }

  // Wall-time pause (post-damage settle) - abortable, not pace-scaled.
  private waitMsRaw(ms: number): Promise<void> {
    return this.sleep(ms);
  }

  // The firing skill's own UI-log icon (assets.textures.logIcon), if it
  // declared one. Undefined -> the log shows just the text.
  private skillLogIcon(skillId: string): Texture | undefined {
    return this.skills?.assets.get(skillId)?.textures.logIcon;
  }

  private async handleEvent(event: BattleEvent): Promise<void> {
    switch (event.type) {
      case 'round_start':
        await this.waitMs(ROUND_START_PAUSE_MS);
        return;

      case 'action_start':
        // dead actors stay down - no idle reset on a corpse
        if (this.deadActors.has(event.actor)) return;
        // Reset to idle to clear any leftover state (dodge offset, prev punch
        // anim). No stance to enter - everyone implicitly attacks.
        await this.adapter.play('idle', this.scene.getSprite(event.actor));
        return;

      case 'action_end':
        if (this.deadActors.has(event.actor)) return;
        await this.adapter.play('idle', this.scene.getSprite(event.actor));
        return;

      case 'attack_start': {
        // Skill-driven swing -> label is the skill's own name + its icon.
        // Plain swing stays "ATTACK". Color is by kind (attack = red) either way.
        const pendingSkill = this.pendingAttackSkill.get(event.actor);
        this.scene.spawnActionLog(
          event.actor,
          pendingSkill ? pendingSkill.engine.name.toUpperCase() : 'ATTACK',
          TEXT_COLOR_ATTACK,
          pendingSkill ? this.skillLogIcon(pendingSkill.engine.id) : undefined,
        );
        this.scene.bringToFront(event.actor);
        const attackerSprite = this.scene.getSprite(event.actor);
        const defender: Actor = event.actor === 'p1' ? 'p2' : 'p1';
        const defenderSprite = this.scene.getSprite(defender);

        // If a skill triggered this attack AND it has a render hook, hand off
        // the entire visual to the skill (skips default lunge + swing). The
        // hook awaits its own internal pacing (anim, projectile flight); the
        // subsequent `damage` event fires at the right moment automatically.
        const skill = this.pendingAttackSkill.get(event.actor);
        this.pendingAttackSkill.delete(event.actor);
        if (skill?.playAttack) {
          const assets = this.skills?.assets.get(skill.engine.id);
          if (assets) {
            // Defender block fires in parallel with the skill anim (no
            // auto-sync - skill timing differs from default punch). By the
            // time the skill's damage lands, defender is in block pose.
            if (event.defenderReaction === 'block') {
              this.adapter.play('block', defenderSprite);
              this.pendingBlockExit.add(defender);
            }
            this.inPlaceAttackers.add(event.actor);
            await skill.playAttack({
              attacker: attackerSprite,
              defender: defenderSprite,
              attackerActor: event.actor,
              defenderActor: defender,
              defenderReaction: event.defenderReaction,
              scene: this.scene,
              assets,
            });
            return;
          }
        }

        // Default attack flow: dash + reactive block sync + swing. The variant
        // (punch / kick) is engine-decided and carried on the event.
        await this.adapter.play('attack_run', attackerSprite);
        if (event.defenderReaction === 'block') {
          // Auto-tune block speed so guard pose lands AT the attack's impact frame.
          const speedOverride = computeBlockSyncSpeed(
            attackerSprite,
            defenderSprite,
            event.defaultAttack,
          );
          this.adapter.play('block', defenderSprite, { speedOverride });
          this.pendingBlockExit.add(defender);
        }
        // Kick sfx is a swing/charge sound - fire it from the FIRST frame of
        // the swing, and regardless of dodge (the leg swings either way).
        if (event.defaultAttack === 'kick') {
          attackerSprite.playSfx('kick');
        }
        await this.adapter.play('attack_swing', attackerSprite, {
          attackAnim: event.defaultAttack,
        });
        // Punch sfx is an impact thud - fire it at the swing's impact frame
        // (attack_swing just resolved there) and skip on dodge (nothing hit).
        if (event.defaultAttack !== 'kick' && event.defenderReaction !== 'dodge') {
          attackerSprite.playSfx(event.defaultAttack);
        }
        return;
      }

      case 'attack_end': {
        // Defender exits BLOCK stance (reverse animation, no hold) in parallel
        // with the attacker's run-back, so both leave the engagement together.
        const defender: Actor = event.actor === 'p1' ? 'p2' : 'p1';
        if (this.pendingBlockExit.has(defender)) {
          this.pendingBlockExit.delete(defender);
          // a defender that died through the block stays down
          if (!this.deadActors.has(defender)) {
            this.adapter.play('block_exit', this.scene.getSprite(defender));
          }
        }
        // Skip retreat for in-place skill attacks - attacker never lunged, so
        // running back to baseX would be running-in-place.
        if (this.inPlaceAttackers.has(event.actor)) {
          this.inPlaceAttackers.delete(event.actor);
          return;
        }
        // attacker killed by a counter does not run home
        if (this.deadActors.has(event.actor)) return;
        await this.adapter.play('attack_return', this.scene.getSprite(event.actor));
        return;
      }

      case 'damage': {
        // Lunge/return are handled by attack_start/attack_end. Here we play
        // the impact reaction + damage number and update HP. Skip the visual
        // when amount === 0 (BLOCKED label conveys it).
        if (event.amount > 0) {
          // HP / damage text / particles fire via onImpact so they sync with
          // the defender's pain.impactFrame (if defined). Without impactFrame,
          // the adapter fires onImpact at the start of pain - matches the
          // old behavior where these visuals appeared immediately.
          const attacker: Actor = event.target === 'p1' ? 'p2' : 'p1';
          const died = event.newHP <= 0;
          if (died) this.deadActors.add(event.target);
          await this.adapter.play('hit', this.scene.getSprite(event.target), {
            launches: event.launches,
            died,
            onImpact: () => {
              this.scene.setHP(event.target, event.newHP);
              this.scene.spawnHeadText(event.target, `-${event.amount}`, TEXT_COLOR_DAMAGE);
              this.scene.spawnDamageParticles(event.target, attacker);
            },
          });
        } else {
          // amount === 0 (full block): no anim, just sync HP silently.
          this.scene.setHP(event.target, event.newHP);
        }
        await this.waitMsRaw(POST_DAMAGE_PAUSE_MS);
        return;
      }

      case 'dodge_success':
        this.scene.spawnActionLog(event.actor, 'DODGE', TEXT_COLOR_DODGE_OK);
        await this.adapter.play('dodge', this.scene.getSprite(event.actor));
        return;

      case 'counter': {
        // Fires for dodge counters and block-skill counters alike (the engine
        // rolls blockSkill.counterChance; no current block skill uses it yet).
        this.scene.spawnActionLog(event.actor, 'COUNTER!', TEXT_COLOR_COUNTER);
        // Counterer is now the active swinger - bring them above the original
        // attacker who's still at lunge peak.
        this.scene.bringToFront(event.actor);
        const counterer = this.scene.getSprite(event.actor);
        // Counter always lands (engine doesn't roll defense against counters).
        // Kick counter = swing sfx from frame 1; punch counter = thud after.
        if (event.defaultAttack === 'kick') counterer.playSfx('kick');
        await this.adapter.play('counter', counterer, { attackAnim: event.defaultAttack });
        if (event.defaultAttack !== 'kick') counterer.playSfx('punch');
        return;
      }

      case 'block': {
        // Block is driven by a block-skill - show that skill's ui-log icon
        // under the BLOCKED label (skill stashed on its earlier skill_trigger).
        const blockSkill = this.pendingBlockSkill.get(event.actor);
        this.pendingBlockSkill.delete(event.actor);
        this.scene.spawnActionLog(
          event.actor,
          'BLOCKED',
          TEXT_COLOR_BLOCKED,
          blockSkill ? this.skillLogIcon(blockSkill.engine.id) : undefined,
        );
        return;
      }

      case 'arena_attach':
      case 'arena_release': {
        // Route to the active arena's render hook. Arena owns its own visuals
        // (and is free to skip - e.g. ThePit just console.logs for now).
        const arena = this.scene.getArena();
        if (arena?.bundle.playArenaEvent) {
          await arena.bundle.playArenaEvent(event, {
            scene: this.scene,
            sheets: arena.sheets,
            textures: arena.textures,
          });
        }
        return;
      }

      case 'arena_explode': {
        // All hits detonated at the same instant - play every reaction
        // CONCURRENTLY (Promise.all), then a single post-damage pause. This is
        // the whole point of arena_explode vs N separate `damage` events: the
        // latter would serialize anim + pause per target.
        await Promise.all(
          event.hits
            .filter((h) => h.amount > 0)
            .map((h) => {
              const attacker: Actor = h.target === 'p1' ? 'p2' : 'p1';
              const died = h.newHP <= 0;
              if (died) this.deadActors.add(h.target);
              return this.adapter.play('hit', this.scene.getSprite(h.target), {
                launches: h.launches,
                died,
                onImpact: () => {
                  this.scene.setHP(h.target, h.newHP);
                  this.scene.spawnHeadText(h.target, `-${h.amount}`, TEXT_COLOR_DAMAGE);
                  this.scene.spawnDamageParticles(h.target, attacker);
                },
              });
            }),
        );
        // Defensive: sync HP for any zero-amount hits (no reaction anim).
        for (const h of event.hits) {
          if (h.amount <= 0) this.scene.setHP(h.target, h.newHP);
        }
        await this.waitMsRaw(POST_DAMAGE_PAUSE_MS);
        return;
      }

      case 'skill_trigger': {
        const bundle = this.skills?.bundles.get(event.skillId);
        if (!bundle) return;
        // Attack-skill: stash so the upcoming attack_start routes to its custom
        // render hook.
        if (bundle.engine.kind === 'attack') {
          this.pendingAttackSkill.set(event.actor, bundle);
          return;
        }
        // Block-skill: stash so the following `block` event can show this
        // skill's ui-log icon (that event carries no skillId).
        if (bundle.engine.kind === 'block') {
          this.pendingBlockSkill.set(event.actor, bundle);
          return;
        }
        // Repair-skill: no attack_start follows - play the in-place self anim
        // now. The subsequent `heal` event pops the +N / fills the HP bar.
        if (bundle.engine.kind === 'repair') {
          this.scene.spawnActionLog(
            event.actor,
            bundle.engine.name.toUpperCase(),
            TEXT_COLOR_HEAL,
            this.skillLogIcon(bundle.engine.id),
          );
          const assets = this.skills?.assets.get(bundle.engine.id);
          if (bundle.playSelf && assets) {
            await bundle.playSelf({
              actor: this.scene.getSprite(event.actor),
              actorSide: event.actor,
              scene: this.scene,
              assets,
            });
          }
        }
        // Buff-skill: in-place charge-up self anim. Non-consuming buffs are
        // followed by the actor's attack (or counter for a defender ADBC buff)
        // - the boosted damage just shows up in the later `damage` event.
        if (bundle.engine.kind === 'buff') {
          this.scene.spawnActionLog(
            event.actor,
            bundle.engine.name.toUpperCase(),
            TEXT_COLOR_BUFF,
            this.skillLogIcon(bundle.engine.id),
          );
          const assets = this.skills?.assets.get(bundle.engine.id);
          if (bundle.playSelf && assets) {
            await bundle.playSelf({
              actor: this.scene.getSprite(event.actor),
              actorSide: event.actor,
              scene: this.scene,
              assets,
            });
          }
        }
        return;
      }

      case 'heal': {
        if (event.amount > 0) {
          this.scene.spawnHeadText(event.actor, `+${event.amount}`, TEXT_COLOR_HEAL);
        }
        this.scene.setHP(event.actor, event.newHP);
        // Same settle pause as a damage hit so the heal reads before the next
        // action.
        await this.waitMsRaw(POST_DAMAGE_PAUSE_MS);
        return;
      }

      case 'round_end':
        await this.waitMs(ROUND_END_PAUSE_MS);
        return;

      case 'battle_end':
        await this.waitMs(BATTLE_END_PAUSE_MS);
        return;
    }
  }
}

// Computes the defender's block animationSpeed so its hold pose aligns in wall
// time with the attacker's impact frame (of whichever default attack - punch
// or kick - is swinging). Returns undefined if either character lacks the
// timings needed (caller falls back to default).
//   We want time(0->holdFrame at blockSpeed) == time(0->impactFrame at atkSpeed)
//   holdFrame / blockSpeed == impactFrame / atkSpeed
//   blockSpeed = holdFrame * atkSpeed / impactFrame
function computeBlockSyncSpeed(
  attacker: ReturnType<BattleScene['getSprite']>,
  defender: ReturnType<BattleScene['getSprite']>,
  attackAnim: DefaultAttack,
): number | undefined {
  const atk = attacker.timings[attackAnim];
  const block = defender.timings.block;
  if (
    atk?.impactFrame === undefined ||
    atk.speed === undefined ||
    block?.holdFrame === undefined
  ) {
    return undefined;
  }
  if (atk.impactFrame <= 0) return undefined;
  return (block.holdFrame * atk.speed) / atk.impactFrame;
}


